import type { Product, CommonStatusUILabel } from '@@partner:types:app'
import type { ProductsFiltersType } from '@components/catalog/product/grid/ProductsFilter'

import { zfill, makeWordsEndings } from './util'
import { formatProductStatusLabel } from '@@partner:helpers/formatters/product'
import {
  makeBetweenFilter,
  makeFilter,
  makeBooleanFilter,
  makeNullFilter,
  makeSearchFilter,
  makePriceFilter
} from '@@partner:helpers/crud/filters'

import {
  PRODUCT_STATUS_DISABLED,
  PRODUCT_STATUS_ENABLED,
  PRODUCT_STATUS_EXCLUDED,
  WEIGHT_MULTIPLIER_G,
  WEIGHT_MULTIPLIER_KG
} from '@@partner:config/product'

interface ProductStatusUISettings extends CommonStatusUILabel {
  value: number
}

/*
  Check product is weight based
*/

export const isWeightProduct = (product: Product) =>
  product.price_type === 'weight'

/*
  Format amount postfix by price type
*/

export const formatAmountPostfix = (priceType?: string) => {
  switch (priceType) {
  case 'piece':
    return 'шт.'
  case 'weight':
    return 'гр.'
  default:
    return ''
  }
}

/*
  Get nearest weight postfix and return formatted
*/

export const findNearestWeightPostfix = (weight = 0) => {
  return weight > 999 ? `${(weight / 1000).toFixed(2)} кг.` : `${weight} гр.`
}

/*
  Product count postfix text
*/

export const formatCountMultiplierPostfix = (product: Product) => {
  switch (product.price_type) {
  case 'piece':
    return 'шт.'
  case 'weight':
    return findNearestWeightPostfix(product.count_multiplier)
  default:
    return ''
  }
}

/*
  Calculates weight multiplier for product
*/

const getNearestWeightMultiplier = (product: Product) => {
  let multiplier = WEIGHT_MULTIPLIER_G

  if (product.weight && product.weight > 1000) {
    multiplier = product.weight_multiplier ?? 0 + 1
  }

  if (multiplier > WEIGHT_MULTIPLIER_KG) {
    return WEIGHT_MULTIPLIER_KG
  }

  return multiplier
}

/*
  Format product weight
*/

export const getProductWeight = (product: Product) => {
  const multiplier = getNearestWeightMultiplier(product)

  if (multiplier !== product.weight_multiplier) {
    return (product?.weight ?? 0 / 1000).toFixed(2)
  }

  return product.weight
}

/*
  Returns product weight postfix
*/

export const getWeightPostfix = (product: Product) => {
  const multiplier = getNearestWeightMultiplier(product)

  switch (multiplier) {
  case WEIGHT_MULTIPLIER_G:
    return 'гр.'
  case WEIGHT_MULTIPLIER_KG:
    return 'кг.'
  default:
    return 'мг.'
  }
}

export const formatWeight = (product: Product) => {
  return `${getProductWeight(product)} ${getWeightPostfix(product)}`
}

/*
  TODO: always return number
*/

export const stockAmount = (
  product: Product,
  defaultValue: string | number = 'Отсутствует на складе'
) => {
  const amount = product?.stock?.amount ?? 0
  return amount > 0 ? amount : defaultValue
}

/*
  Product count postfix text
*/

export const getCountPostfix = (product: Product, withMinAmount = true) => {
  switch (product.price_type) {
  case 'piece':
    return 'шт.'
  case 'weight':
    const prefix = withMinAmount && getMinAmount(product) + ' '
    return (prefix || '') + 'гр.'
  default:
    return ''
  }
}

/*
  Minmal amount formula
*/

export const getMinAmount = (product: Product) => product.min_count as number

export const getAmountStep = (product: Product) => product.min_count as number

export const calculateSalePercent = (price: number, oldPrice: number) => {
  if (price < oldPrice) {
    const percent = (1 - price / oldPrice) * 100
    if (!percent || percent <= 0 || isNaN(percent)) {
      return 0
    }

    return Math.ceil(percent) //.toFixed(2)
  }

  return 0
}

export const formatSaleFinishDate = (dateString: string) => {
  const date = new Date(parseInt(dateString))

  if (date) {
    return `${zfill(date.getDate())}.${zfill(date.getMonth() + 1)}`
  }
  return ''
}

export const isValidEan = (input: string) => {
  return /\b(?:\d{8}|\d{13})\b/.test(input)
}

export function getSearchableProductStatuses (): ProductStatusUISettings[] {
  const SEARCHABLE_PRODUCT_STATUSES = [
    PRODUCT_STATUS_EXCLUDED,
    PRODUCT_STATUS_DISABLED,
    PRODUCT_STATUS_ENABLED
  ]

  return SEARCHABLE_PRODUCT_STATUSES.map((statusCode) => {
    const statusConfig = formatProductStatusLabel(statusCode)

    return {
      value: statusCode,
      label: statusConfig.label,
      color: statusConfig.color
    }
  })
}

/*
  Calculate and format average product rating
*/

export const formatAverageRating = (average_rating: number) => {
  const ratingsWords = [ ' отзыв', ' отзыва', ' отзывов' ]

  return makeWordsEndings(average_rating, ratingsWords)
}

export const prepareProductsFilter = (filters: ProductsFiltersType) => {
  const idsFilter = makeFilter('id', 'contains', filters.ids)
  const articleFiler = makeFilter('article_num', 'contains', filters.xid)
  const xidFilter = makeFilter('xid', 'contains', filters.xid)
  const eanFilter = makeFilter('ean', 'contains', filters.xid)
  const createdAtFilter = makeBetweenFilter('created_at', filters.createdAt)
  const updatedAtFilter = makeBetweenFilter('updated_at', filters.updatedAt)
  const stockDateFilter = makeFilter('stockDate', 'lt', filters.stockDate.to)
  const stockFilter = makePriceFilter('stock', {
    priceFrom: Number(filters.stockFrom),
    priceTo: Number(filters.stockTo)
  })
  const statusFilter = makeFilter('status', 'contains', filters.statuses)
  const photoFilter = makeNullFilter('preview_id', (typeof filters.withPhoto === 'boolean') ? filters.withPhoto : undefined)
  const indexableFilter = makeBooleanFilter('is_indexable', (typeof filters.indexable === 'boolean') ? filters.indexable : undefined)
  const priceTypeFilter = makeFilter('price_type', 'contains', filters.price_type)
  const searchFilter = makeSearchFilter('name', 'starts_with', filters.name)
  const priceFilter = makePriceFilter('price', {
    priceFrom: Number(filters.priceFrom),
    priceTo: Number(filters.priceTo)
  })
  const labelsFilter = makeFilter(
    'marketingLabelId',
    'contains',
    filters.marketingLabelId
  )
  const categoriesFilter = makeFilter('categoryIds', 'contains', filters.categoryIds)
  const brandIdsFilter = makeFilter('brand', 'contains', filters.brandIds)
  const productGroupIdsFilter = makeFilter('productGroupIds', 'contains', filters.productGroupIds)

  return {
    and: [
      idsFilter,
      {
        or: [
          xidFilter,
          articleFiler,
          eanFilter
        ]
      },
      createdAtFilter,
      updatedAtFilter,
      stockDateFilter,
      statusFilter,
      photoFilter,
      indexableFilter,
      priceTypeFilter,
      searchFilter,
      priceFilter,
      labelsFilter,
      categoriesFilter,
      brandIdsFilter,
      productGroupIdsFilter,
      stockFilter
    ]
  }
}
